import { useQuery } from '@tanstack/react-query';
import { fetchEngagementSnapshots } from '../services/outreach/engagementAnalyticsService';
import type { CampaignStats } from '../services/outreach/types';
import { roundToTwo } from '../utils/engagementAnalytics';

export interface SESReputationData {
  bounceRate: number;
  complaintRate: number;
  engagementRate: number;
  deliveryRate: number;
  reputationScore: number;
  lastCalculated: string;
  totalEmailsSent: number;
  lifetimeOpenRate: number;
  lifetimeClickRate: number;
  lifetimeDeliveryRate: number;
}

const aggregateStats = (stats: CampaignStats[]) => {
  return stats.reduce(
    (acc, stat) => {
      acc.sent += stat.totalSent ?? 0;
      acc.delivered += stat.delivered ?? 0;
      acc.opened += stat.opened ?? 0;
      acc.clicked += stat.clicked ?? 0;
      acc.bounced += stat.bounced ?? 0;
      acc.complained += stat.complained ?? 0;
      return acc;
    },
    {
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      complained: 0,
    }
  );
};

const buildReputationSnapshot = async (): Promise<SESReputationData> => {
  const snapshots = await fetchEngagementSnapshots({
    includeStats: true,
    includeTracking: false,
  });

  const stats = snapshots
    .map((snapshot) => snapshot.stats)
    .filter((value): value is CampaignStats => Boolean(value));

  const totals = aggregateStats(stats);

  const bounceRate =
    totals.sent > 0 ? roundToTwo((totals.bounced / totals.sent) * 100) : 0;
  const complaintRate =
    totals.delivered > 0
      ? roundToTwo((totals.complained / totals.delivered) * 100)
      : 0;
  const engagementRate =
    totals.delivered > 0
      ? roundToTwo(((totals.opened + totals.clicked) / totals.delivered) * 100)
      : 0;
  const deliveryRate =
    totals.sent > 0 ? roundToTwo((totals.delivered / totals.sent) * 100) : 0;

  const bounceScore = Math.max(0, 100 - bounceRate * 20);
  const complaintScore = Math.max(0, 100 - complaintRate * 100);
  const engagementScore = Math.min(100, engagementRate * 2);

  const reputationScore = roundToTwo(
    bounceScore * 0.4 + complaintScore * 0.4 + engagementScore * 0.2
  );

  // Calculate lifetime open rate and click rate
  const lifetimeOpenRate = totals.delivered > 0 
    ? roundToTwo((totals.opened / totals.delivered) * 100) / 100 
    : 0;
  const lifetimeClickRate = totals.delivered > 0 
    ? roundToTwo((totals.clicked / totals.delivered) * 100) / 100 
    : 0;
  const lifetimeDeliveryRate = totals.sent > 0 
    ? roundToTwo((totals.delivered / totals.sent) * 100) / 100 
    : 0;

  return {
    bounceRate,
    complaintRate,
    engagementRate,
    deliveryRate,
    reputationScore,
    lastCalculated: new Date().toISOString(),
    totalEmailsSent: totals.sent,
    lifetimeOpenRate,
    lifetimeClickRate,
    lifetimeDeliveryRate,
  };
};

export const useSESReputation = () =>
  useQuery({
    queryKey: ['ses-reputation'],
    queryFn: buildReputationSnapshot,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

